import type { EditorScreen } from '@/store/editorSlice';
import { getStoredUtm } from '@/utils/analytics/utmCapture';
import { resolveOutboundClickIdMacro } from '@/utils/analytics/clickIdCapture';
import { getVisitorId } from '@/utils/analytics/visitorId';
import { resolvePlayerConsent } from '@/utils/analytics/playerPixelEvents';
import { resolveSharingConsent } from '@/utils/analytics/euConsent';
import { collectOutboundAnswers } from './outboundAnswers';

/**
 * WEB-2123 — ЗНАЧЕНИЯ МЕТОК ДЛЯ ИСХОДЯЩЕГО АДРЕСА.
 *
 * Их везёт плеер, потому что взять их серверу негде: метки кампании и клика
 * лежат в хранилище на нашем хосте (куки `qb_utm_lt`, `_aff_click_id`), а
 * точка ухода — отдельный запрос, в адресе которого рекламных меток уже нет.
 *
 * ОТКАЗАВШИЙ В СОГЛАСИИ НЕ ОТДАЁТ НИЧЕГО. Это строже, чем у аналитики: там
 * арбитр сервер, и вне ЕС решения не спрашивают вовсе. Здесь значение уезжает
 * НА ЧУЖОЙ сервер — в его журналы и в историю браузера, — и арбитром быть
 * некому. Нажавший «Отклонить» вправе рассчитывать, что не уедет ничего.
 * Решения нет (посетитель из ЕС ещё не ответил на баннер) — метки кампании,
 * клика и ответы едут, а постоянный идентификатор посетителя — нет: он едет
 * только при согласии (WEB-3795, решение владельца 23.10.2026). Вне ЕС
 * согласие не спрашивают, но страница сама записывает его до любого ухода
 * (`PublicQuiz.tsx`, `PublicPaywall.tsx`), так что там это тот же `granted`.
 *
 * На сам переход отказ не влияет: человек обязан дойти до оплаты (п.6
 * требований WEB-2164).
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx без изменений поведения. Плеер
 * зовёт эту функцию для ОБЕИХ веток ухода (серверная точка и кнопка-ссылка
 * через `toOutboundMacroValues`) — гейт согласия по-прежнему один.
 */
export function quizOutboundMacroValues(
  screens: EditorScreen[],
  values: Record<string, string>,
  outboundAnswersBlocked: boolean | undefined,
) {
  // Ревизия 45 (C3): сигнал браузера Global Privacy Control — это и есть
  // «не передавать мои данные третьим лицам», а уход на чужой сервер и есть
  // передача. Правило берём готовое (`resolveSharingConsent`), тем же
  // вызовом, что и аналитика, — своя копия гейта разошлась бы молча
  // (шрам WEB-953).
  const sharing = resolveSharingConsent(resolvePlayerConsent());
  if (sharing === 'denied') return {};
  return {
    utm: getStoredUtm(),
    // Ревизия 46 (Н3): значение метки собирается ОТДЕЛЬНОЙ функцией, а не
    // читается из партнёрской куки напрямую. Партнёрский клик — как раньше;
    // `fbclid` подставляется только при его полном отсутствии и только
    // СЮДА, в адрес, не попадая в куку выплат партнёру.
    clickId: resolveOutboundClickIdMacro(),
    // Проход по швам пачки 45 (С3): постоянный идентификатор посетителя как
    // ЗНАЧЕНИЕ метки `{visitor_id}` едет только отсюда, под гейтом согласия
    // (каким — ниже, WEB-3795). Раньше сервер подставлял
    // его из `v` (ключа нашего учёта), и под сигналом GPC идентификатор
    // человека всё равно уезжал на чужой сервер: гейт закрывал метки
    // кампании и ответы, а самого человека — нет.
    // WEB-3795 (решение владельца 23.10.2026): только при СОГЛАСИИ, а не
    // «пока не запрещено», как было до этой карточки. Посетитель из ЕС, не ответивший на баннер, уходит
    // без `mv` — метка `{visitor_id}` в чужом адресе пустая; ключа нет вовсе,
    // и `getVisitorId()` без согласия не зовётся. То же правило, что у ключа
    // нашего учёта `v` (WEB-3296, `resolveExitVisitorFields`).
    ...(sharing === 'granted' ? { macroVisitorId: getVisitorId() } : {}),
    // Фильтр по галочке автора — тот же набор типов, что у сервера; сервер
    // потом ещё раз отфильтрует по ОПУБЛИКОВАННОМУ содержимому, поэтому
    // подделать состав ответов из браузера нельзя.
    // Свежий взгляд пачки 46 (находка 2): плюс запрет по НИШЕ — готовым
    // признаком от сервера. Он нужен именно здесь: значения отсюда идут в
    // ОБЕ ветки ухода, включая кнопку-ссылку, которую серверный фильтр не
    // видит по построению (WEB-2164 п.6).
    answers: collectOutboundAnswers(screens, values, outboundAnswersBlocked),
  };
}
